import React from 'react'
import { useFormikContext } from 'formik'
import { BasicTextFieldProps } from '../BasicTextField'
import { UploadedFile } from '../../UploadedFile'
import { FileUploadInput } from '../../FileUploadInput'
import { FormControl, FormHelperText, Typography } from '@mui/material'
import tw from 'twin.macro'
import { RiskMinor } from '@shopify/polaris-icons'

const ACCESS_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
// 10 MB
const DEFAULT_FILE_SIZE_LIMIT = 10000000

const deriveUploadedFileRemoveTestId = (inputDataTestId?: string) =>
  inputDataTestId?.endsWith('-input')
    ? inputDataTestId.replace(/-input$/, '-remove-btn')
    : undefined

/**
 * `acceptTypes`, `caption` and `fileSizeLimit` are optional and default to the original
 * image-or-PDF, 10MB behaviour, so existing callers are unchanged. Pass them when a field's
 * contract is narrower — otherwise the picker's caption would advertise types the backend
 * rejects, and a size limit the API does not document.
 */
export const FormikFileInput = <T extends unknown>({
  name,
  label,
  inputDataTestId,
  uploadedFileRemoveDataTestId,
  acceptTypes = ACCESS_TYPES,
  caption,
  fileSizeLimit = DEFAULT_FILE_SIZE_LIMIT,
}: BasicTextFieldProps<T> & {
  inputDataTestId?: string
  uploadedFileRemoveDataTestId?: string
  acceptTypes?: Array<string>
  caption?: string
  /** Pass `undefined` to enforce no client-side size limit. */
  fileSizeLimit?: number
}) => {
  const removeTestId =
    uploadedFileRemoveDataTestId ?? deriveUploadedFileRemoveTestId(inputDataTestId)
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<T>()

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined

  return (
    <div>
      {label ? (
        // Same label as BasicTextField, so a file field lines up with the text fields around it.
        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
          {label}
        </Typography>
      ) : null}
      <FormControl fullWidth error={!!error}>
        {values[name] ? (
          <UploadedFile
            file={values[name] as any}
            onRemove={() => {
              setFieldTouched(name as string, false)
              setFieldValue(name as string, null)
            }}
            removeButtonTestId={removeTestId}
          />
        ) : (
          <FileUploadInput
            fileSizeLimit={fileSizeLimit}
            acceptTypes={acceptTypes}
            caption={caption}
            inputDataTestId={inputDataTestId}
            onSelect={(file) => {
              setFieldValue(name as string, file)
            }}
          />
        )}
        {isTouched && error ? (
          <FormHelperText component="div" css={tw`ml-0`}>
            <div css={tw`flex items-center gap-1`}>
              <RiskMinor width={20} css={tw`fill-critical-500`} />
              <Typography variant="body2" css={tw`text-critical-500`} component="span">
                {error}
              </Typography>
            </div>
          </FormHelperText>
        ) : null}
      </FormControl>
    </div>
  )
}
